import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group'
import { Lock, Users, Building2, FileText, Database, Globe } from 'lucide-react'
import { type AgentBuilderState } from '../AgentBuilder'

interface ScopeStepProps {
  state: AgentBuilderState
  updateState: (updates: Partial<AgentBuilderState>) => void
}

const ACCESS_LEVELS = [
  {
    value: 'private' as const,
    label: 'Private',
    description: 'Only you can use this agent',
    icon: Lock,
  },
  {
    value: 'team' as const,
    label: 'Team',
    description: 'Share with your team members',
    icon: Users,
  },
  {
    value: 'organization' as const,
    label: 'Organization',
    description: 'Available to entire organization',
    icon: Building2,
  },
]

const DOCUMENT_TYPES = [
  { id: 'proposals', label: 'Proposals', description: 'Business proposals and quotes' },
  { id: 'contracts', label: 'Contracts', description: 'Legal contracts and agreements' },
  { id: 'reports', label: 'Reports', description: 'Business and financial reports' },
  { id: 'presentations', label: 'Presentations', description: 'Slide decks and presentations' },
  { id: 'emails', label: 'Emails', description: 'Email templates and communications' },
  { id: 'marketing', label: 'Marketing', description: 'Marketing materials and copy' },
]

export function ScopeStep({ state, updateState }: ScopeStepProps) {
  const handleDocumentTypeToggle = (typeId: string) => {
    const current = state.documentTypes
    const updated = current.includes(typeId)
      ? current.filter((t) => t !== typeId)
      : [...current, typeId]
    updateState({ documentTypes: updated })
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Scope & Permissions
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Define who can access this agent and what content it can work with.
        </p>
      </div>

      {/* Access Level */}
      <div className="space-y-4">
        <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
          Access Level
        </Label>
        <RadioGroup
          value={state.accessLevel}
          onValueChange={(value: 'private' | 'team' | 'organization') =>
            updateState({ accessLevel: value })
          }
          className="grid gap-4"
        >
          {ACCESS_LEVELS.map((level) => {
            const Icon = level.icon
            const isSelected = state.accessLevel === level.value

            return (
              <label
                key={level.value}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <RadioGroupItem value={level.value} className="sr-only" />
                <div
                  className={`p-3 rounded-xl ${
                    isSelected
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white">{level.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{level.description}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                  }`}
                >
                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
              </label>
            )
          })}
        </RadioGroup>
      </div>

      {/* Document Types */}
      <div className="space-y-4">
        <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
          Document Types
        </Label>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select the types of documents this agent can work with.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          {DOCUMENT_TYPES.map((type) => {
            const isSelected = state.documentTypes.includes(type.id)

            return (
              <label
                key={type.id}
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                  isSelected
                    ? 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleDocumentTypeToggle(type.id)}
                  className="mt-0.5"
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{type.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{type.description}</p>
                </div>
              </label>
            )
          })}
        </div>
      </div>

      {/* Data Access */}
      <div className="space-y-4">
        <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
          Data Access
        </Label>
        <div className="space-y-4">
          <div
            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
              state.canAccessContent
                ? 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Content Access</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Agent can read and analyze document content
                </p>
              </div>
            </div>
            <Switch
              checked={state.canAccessContent}
              onCheckedChange={(checked) => updateState({ canAccessContent: checked })}
            />
          </div>

          <div
            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
              state.canAccessData
                ? 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <Database className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Data Sources Access</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Agent can connect to external data sources
                </p>
              </div>
            </div>
            <Switch
              checked={state.canAccessData}
              onCheckedChange={(checked) => updateState({ canAccessData: checked })}
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
          Scope Summary
        </p>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm">
            {state.accessLevel === 'private' && <Lock className="h-3.5 w-3.5" />}
            {state.accessLevel === 'team' && <Users className="h-3.5 w-3.5" />}
            {state.accessLevel === 'organization' && <Building2 className="h-3.5 w-3.5" />}
            {ACCESS_LEVELS.find((l) => l.value === state.accessLevel)?.label}
          </div>
          {state.documentTypes.length > 0 && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm">
              <FileText className="h-3.5 w-3.5" />
              {state.documentTypes.length} document type{state.documentTypes.length !== 1 && 's'}
            </div>
          )}
          {state.canAccessContent && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm">
              <Globe className="h-3.5 w-3.5" />
              Content access
            </div>
          )}
          {state.canAccessData && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm">
              <Database className="h-3.5 w-3.5" />
              Data sources
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
